/**
 * Desktop Content Components
 * Extracted from HubDashboard.jsx
 */

import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../../../components/ui/button';
import {
  Coins, UserCheck, Award, Calendar, FileText, Upload,
  Truck, RefreshCw, Search, Download, Plus, Eye, Trash2,
  Palette, Lock, AlertTriangle
} from 'lucide-react';
import SecurityMonitor from '../../SecurityMonitor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Points Content
export function DesktopPointsContent({ user }) {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Loyalty Points</h2>
      <div className="bg-gradient-to-br from-amber-500/20 to-orange-500/10 rounded-3xl p-8 border border-amber-500/30">
        <div className="text-center">
          <Coins className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          <p className="text-5xl font-bold text-amber-400">{user?.points || 0}</p>
          <p className="text-muted-foreground mt-2">Available Points</p>
        </div>
      </div>
      <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
        <h3 className="font-bold mb-4">How to Earn Points</h3>
        <ul className="space-y-3 text-muted-foreground">
          <li className="flex items-center gap-3">
            <span className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center text-green-400">✓</span>
            Complete training videos
          </li>
          <li className="flex items-center gap-3">
            <span className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400">📝</span>
            Submit PTI inspections
          </li>
          <li className="flex items-center gap-3">
            <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">🎯</span>
            Achieve safety milestones
          </li>
        </ul>
      </div>
    </div>
  );
}

// Approvals Content
export function DesktopApprovalsContent() {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/users?status=pending`, { withCredentials: true });
        setPending(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load pending users');
      } finally {
        setLoading(false);
      }
    };
    fetchPending();
  }, []);

  const approveUser = async (userId) => {
    try {
      await axios.put(`${API}/users/${userId}`, { status: 'active' }, { withCredentials: true });
      toast.success('User approved!');
      setPending(pending.filter(u => u.user_id !== userId));
    } catch (e) {
      toast.error('Failed to approve user');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold flex items-center gap-2">
        <UserCheck className="w-7 h-7 text-accent" /> Pending Approvals
      </h2>
      {pending.length === 0 ? (
        <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
          <UserCheck className="w-20 h-20 text-green-400 mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-3">All Caught Up!</h3>
          <p className="text-muted-foreground">No pending user approvals.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pending.map((u) => (
            <div key={u.user_id} className="bg-card/60 rounded-2xl p-5 border border-border/30 flex items-center justify-between">
              <div>
                <p className="font-bold">{u.name}</p>
                <p className="text-sm text-muted-foreground">{u.email}</p>
              </div>
              <Button onClick={() => approveUser(u.user_id)} className="bg-green-500 hover:bg-green-600">
                Approve
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Kudos Content
export function DesktopKudosContent() {
  const [kudos, setKudos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGiveKudos, setShowGiveKudos] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [kudosCategory, setKudosCategory] = useState('Great Work');
  const [kudosMessage, setKudosMessage] = useState('');

  const categories = ['Great Work', 'Team Player', 'Problem Solver', 'Leadership', 'Innovation', 'Customer Focus', 'Safety First'];

  useEffect(() => {
    fetchKudos();
    fetchUsers();
  }, []);

  const fetchKudos = async () => {
    try {
      const res = await axios.get(`${API}/social/kudos`, { withCredentials: true });
      setKudos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load kudos');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load users');
    }
  };

  const sendKudos = async () => {
    if (!selectedUser || !kudosMessage) {
      toast.error('Please select a user and add a message');
      return;
    }
    try {
      await axios.post(`${API}/social/kudos`, {
        recipient_id: selectedUser,
        category: kudosCategory,
        message: kudosMessage
      }, { withCredentials: true });
      toast.success('Kudos sent! 🎉');
      setShowGiveKudos(false);
      setSelectedUser('');
      setKudosMessage('');
      fetchKudos();
    } catch (e) {
      toast.error('Failed to send kudos');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  const safeKudos = Array.isArray(kudos) ? kudos : [];

  return (
    <div className="space-y-6" data-testid="kudos-content">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Kudos Wall</h2>
        <Button onClick={() => setShowGiveKudos(!showGiveKudos)} className="gap-2 bg-amber-500 hover:bg-amber-600">
          <Award className="w-4 h-4" /> Give Kudos
        </Button>
      </div>

      {showGiveKudos && (
        <div className="bg-amber-500/10 rounded-2xl p-6 border border-amber-500/30">
          <h3 className="font-bold mb-4">Send Recognition</h3>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Select Team Member</label>
              <select 
                value={selectedUser} 
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              >
                <option value="">Choose someone...</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Category</label>
              <select 
                value={kudosCategory} 
                onChange={(e) => setKudosCategory(e.target.value)}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-muted-foreground mb-1 block">Message</label>
              <textarea 
                value={kudosMessage}
                onChange={(e) => setKudosMessage(e.target.value)}
                placeholder="Why do they deserve recognition?"
                className="w-full p-3 rounded-xl bg-secondary border border-border/30 min-h-[100px]"
              />
            </div>
            <Button onClick={sendKudos} className="w-full bg-amber-500 hover:bg-amber-600">Send Kudos 🎉</Button>
          </div>
        </div>
      )}

      {safeKudos.length === 0 ? (
        <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
          <Award className="w-20 h-20 text-amber-400 mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-3">No Kudos Yet</h3>
          <p className="text-muted-foreground">Be the first to recognize a team member!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {safeKudos.map((k, i) => (
            <div key={i} className="bg-card/60 rounded-2xl p-5 border border-border/30">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold">{k.sender_name}</span>
                    <span className="text-muted-foreground">→</span>
                    <span className="font-bold text-amber-400">{k.recipient_name}</span>
                  </div>
                  <span className="text-xs px-2 py-1 bg-amber-500/20 text-amber-400 rounded-full">{k.category}</span>
                  <p className="text-muted-foreground mt-2">{k.message}</p>
                  <p className="text-xs text-muted-foreground/50 mt-2">{new Date(k.created_at).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Teams Content (placeholder)
export function DesktopTeamsContent() {
  return (
    <div className="space-y-6" data-testid="teams-content">
      <h2 className="text-2xl font-bold">Teams</h2>
      <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
        <p className="text-muted-foreground">Teams feature coming soon...</p>
      </div>
    </div>
  );
}

// Calendar Content
export function DesktopCalendarContent() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', date: '', time: '', description: '' });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/social/events`, { withCredentials: true });
      setEvents(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const addEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Title and date are required');
      return;
    }
    try {
      await axios.post(`${API}/social/events`, {
        title: newEvent.title,
        start_date: `${newEvent.date}T${newEvent.time || '09:00'}:00`,
        description: newEvent.description
      }, { withCredentials: true });
      toast.success('Event created!');
      setShowAddEvent(false);
      setNewEvent({ title: '', date: '', time: '', description: '' });
      fetchEvents();
    } catch (e) {
      toast.error('Failed to create event');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  const safeEvents = Array.isArray(events) ? events : [];

  return (
    <div className="space-y-6" data-testid="calendar-content">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Calendar</h2>
        <Button onClick={() => setShowAddEvent(!showAddEvent)} className="gap-2">
          <Plus className="w-4 h-4" /> Add Event
        </Button>
      </div>

      {showAddEvent && (
        <div className="bg-accent/10 rounded-2xl p-6 border border-accent/30">
          <h3 className="font-bold mb-4">New Event</h3>
          <div className="space-y-4">
            <input
              type="text"
              placeholder="Event title"
              value={newEvent.title}
              onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
              className="w-full p-3 rounded-xl bg-secondary border border-border/30"
            />
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={newEvent.date}
                onChange={(e) => setNewEvent({...newEvent, date: e.target.value})}
                className="p-3 rounded-xl bg-secondary border border-border/30"
              />
              <input
                type="time"
                value={newEvent.time}
                onChange={(e) => setNewEvent({...newEvent, time: e.target.value})}
                className="p-3 rounded-xl bg-secondary border border-border/30"
              />
            </div>
            <textarea
              placeholder="Description (optional)"
              value={newEvent.description}
              onChange={(e) => setNewEvent({...newEvent, description: e.target.value})}
              className="w-full p-3 rounded-xl bg-secondary border border-border/30 min-h-[80px]"
            />
            <Button onClick={addEvent} className="w-full">Create Event</Button>
          </div>
        </div>
      )}

      {safeEvents.length === 0 ? (
        <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
          <Calendar className="w-20 h-20 text-accent mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-3">No Events</h3>
          <p className="text-muted-foreground">Create your first event!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {safeEvents.map((event, i) => (
            <div key={i} className="bg-card/60 rounded-2xl p-5 border border-border/30">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <p className="font-bold">{event.title}</p>
                  <p className="text-sm text-muted-foreground">{new Date(event.start_date).toLocaleString()}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Documents Content
export function DesktopDocumentsContent() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API}/social/documents`, { withCredentials: true });
      setDocuments(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/social/documents/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded!');
      fetchDocuments();
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-accent" /></div>;

  const safeDocuments = Array.isArray(documents) ? documents : [];

  return (
    <div className="space-y-6" data-testid="documents-content">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Documents</h2>
        <label className="cursor-pointer">
          <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          <Button as="span" className="gap-2" disabled={uploading}>
            <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </label>
      </div>

      {safeDocuments.length === 0 ? (
        <div className="bg-card/60 rounded-3xl p-12 border border-border/30 text-center">
          <FileText className="w-20 h-20 text-accent mx-auto mb-6" />
          <h3 className="text-2xl font-bold mb-3">No Documents</h3>
          <p className="text-muted-foreground">Upload your first document!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {safeDocuments.map((doc, i) => (
            <div key={i} className="bg-card/60 rounded-2xl p-5 border border-border/30">
              <FileText className="w-10 h-10 text-accent mb-3" />
              <p className="font-medium truncate">{doc.filename}</p>
              <p className="text-xs text-muted-foreground">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Apps Content
export function DesktopAppsContent({ handleAppLaunch, getFilteredApps, user }) {
  const apps = getFilteredApps(user?.role_id, user?.connected_apps);
  
  return (
    <div className="space-y-6" data-testid="apps-content">
      <h2 className="text-2xl font-bold">Connected Apps</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {apps.map((app) => {
          const Icon = app.icon;
          return (
            <button
              key={app.id}
              onClick={() => handleAppLaunch(app)}
              className="bg-card/60 rounded-2xl p-6 border border-border/30 hover:border-accent/50 transition-all text-left group"
            >
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: `${app.color}20` }}>
                <Icon className="w-7 h-7" style={{ color: app.color }} />
              </div>
              <p className="font-bold">{app.name}</p>
              <p className="text-sm text-muted-foreground mt-1 group-hover:text-accent transition-colors">Launch →</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Settings Content
export function DesktopSettingsContent({ theme, setTheme, themes, profile, setProfile, user }) {
  const [settingsTab, setSettingsTab] = useState('profile');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { withCredentials: true });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const isMasterAdmin = user?.role_id === 'master_admin';

  return (
    <div className="space-y-6" data-testid="settings-content">
      <h2 className="text-2xl font-bold">Settings</h2>
      
      {/* Settings Tabs */}
      <div className="flex gap-2 border-b border-border/30 pb-4">
        <button
          onClick={() => setSettingsTab('profile')}
          className={`px-4 py-2 rounded-lg font-medium ${settingsTab === 'profile' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
        >
          Profile
        </button>
        <button
          onClick={() => setSettingsTab('appearance')}
          className={`px-4 py-2 rounded-lg font-medium ${settingsTab === 'appearance' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
        >
          Appearance
        </button>
        {isMasterAdmin && (
          <button
            onClick={() => setSettingsTab('security')}
            className={`px-4 py-2 rounded-lg font-medium ${settingsTab === 'security' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
          >
            Security Monitor
          </button>
        )}
      </div>

      {/* Profile Tab */}
      {settingsTab === 'profile' && (
        <div className="space-y-6">
          <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
            <h3 className="font-bold mb-4">Profile Information</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground">Name</label>
                <p className="font-medium">{profile?.name || user?.name}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Email</label>
                <p className="font-medium">{profile?.email || user?.email}</p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Role</label>
                <p className="font-medium capitalize">{(profile?.role_id || user?.role_id)?.replace('_', ' ')}</p>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
            <h3 className="font-bold mb-4 flex items-center gap-2">
              <Lock className="w-5 h-5" /> Change Password
            </h3>
            <div className="space-y-4">
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-500 inline mr-2" />
                Password must be at least 8 characters with uppercase, lowercase, and number.
              </div>
              <input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
              <input
                type="password"
                placeholder="New password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
              <input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full p-3 rounded-xl bg-secondary border border-border/30"
              />
              <Button 
                onClick={handlePasswordChange} 
                disabled={changingPassword || !currentPassword || !newPassword}
                className="w-full"
              >
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Appearance Tab */}
      {settingsTab === 'appearance' && (
        <div className="bg-card/60 rounded-2xl p-6 border border-border/30">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Palette className="w-5 h-5" /> Theme
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {Object.values(themes).map((t) => (
              <button
                key={t.id}
                onClick={() => setTheme(t.id)}
                className={`p-4 rounded-xl border-2 transition-all ${theme === t.id ? 'border-accent' : 'border-transparent hover:border-border'}`}
                style={{ background: t.colors.bg }}
              >
                <div className="w-8 h-8 rounded-full mx-auto mb-2" style={{ backgroundColor: t.colors.accent }} />
                <p className="text-xs text-center" style={{ color: t.colors.text }}>{t.name}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Security Tab - Master Admin Only */}
      {settingsTab === 'security' && isMasterAdmin && (
        <SecurityMonitor />
      )}
    </div>
  );
}
